import React from 'react';
import {
  Heading,
  Box,
  Flex,
  Button,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Checkbox,
  Stack,
} from '@chakra-ui/react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import xhr from 'xhr';
import BasicModal from 'components/modal/Modal';
import globals from 'styles/globals.module.css';
import useAuth from 'hooks/useAuth';

const statusMap = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  denied: 'Rechazado',
};

const methodMap = {
  scanned: 'Escaneado',
  fisical: 'Físico',
};

const stripHtml = (html) => {
  if (!html) return '';
  return String(html).replace(/<[^>]+>/g, '').trim();
};

export default function VerRequisitos() {
  const params = useParams();
  const id_est = params.id;

  const [searchParams] = useSearchParams();

  const [requeriments, setRequeriments] = React.useState([]);
  const [programs, setPrograms] = React.useState([]);

  const [imageUrl, setImageUrl] = React.useState('');
  const [method, setMethod] = React.useState('');
  const [requirementSelect, setRequirementSelect] = React.useState('');
  const [approvedPreviousYear, setApprovedPreviousYear] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);

  const [duplicateOpen, setDuplicateOpen] = React.useState(false);
  const [duplicateLoading, setDuplicateLoading] = React.useState(false);
  const [requirementToDuplicate, setRequirementToDuplicate] = React.useState(null);

  const [programTypes, setProgramTypes] = React.useState([]);
  const [targetProgramKey, setTargetProgramKey] = React.useState('');
  const [targetRequirements, setTargetRequirements] = React.useState([]);

  const [duplicateMode, setDuplicateMode] = React.useState('single'); // single | many
  const [targetRequirement, setTargetRequirement] = React.useState('');
  const [targetRequirementsMany, setTargetRequirementsMany] = React.useState([]);

  const inputEl = React.useRef(null);

  const { user } = useAuth();
  const toast = useToast();

  const currentType = searchParams.get('type');
  const currentProgram = searchParams.get('program');
  const currentProgramType = searchParams.get('programType');

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageUrl(file);
  };

  const loadCurrentRequirements = React.useCallback(async () => {
    try {
      const res = await xhr(
        `/requirement-sittings?filters[student][id][$eq]=${id_est}&filters[program_type][id][$eq]=${currentProgramType}&populate=*`
      );

      setRequeriments(res?.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  }, [id_est, currentProgramType]);

  React.useEffect(() => {
    const getPrograms = async () => {
      try {
        const programsResponse = await xhr(
          `/${currentType.replace('_', '-')}s?filters[id][$eq]=${currentProgram}&populate=requirements`
        );

        const requirements = programsResponse?.data?.data?.[0]?.requirements || [];

        setPrograms(
          requirements.map((el) => ({
            ...el,
            label: el.name,
            value: el.id,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };

    if (currentType && currentProgram) {
      getPrograms();
    }
  }, [currentType, currentProgram]);

  React.useEffect(() => {
    if (id_est && currentProgramType) {
      loadCurrentRequirements();
    }
  }, [id_est, currentProgramType, loadCurrentRequirements]);

  React.useEffect(() => {
    const getStudentProgramsForDuplicate = async () => {
      try {
        const [gradeRes, technicalRes] = await Promise.all([
          xhr(
            `/grade-enrollments?filters[student][id][$eq]=${id_est}&populate=grade,grade.program_type,grade.requirements`
          ),
          xhr(
            `/technical-career-enrollments?filters[student][id][$eq]=${id_est}&populate=technical_career,technical_career.program_type,technical_career.requirements`
          ),
        ]);

        const gradeOptions = (gradeRes?.data?.data || [])
          .filter((item) => item.grade && item.grade.program_type?.id)
          .map((item) => ({
            key: `grade-${item.grade.id}`,
            source: 'grade',
            enrollmentId: item.id,
            programId: item.grade.id,
            programName: item.grade.name || `Grado ${item.grade.id}`,
            programTypeId: item.grade.program_type?.id,
            programTypeName: item.grade.program_type?.name || '',
            requirements: item.grade.requirements || [],
          }));

        const technicalOptions = (technicalRes?.data?.data || [])
          .filter((item) => item.technical_career && item.technical_career.program_type?.id)
          .map((item) => ({
            key: `technical-${item.technical_career.id}`,
            source: 'technical',
            enrollmentId: item.id,
            programId: item.technical_career.id,
            programName: item.technical_career.name || `Programa ${item.technical_career.id}`,
            programTypeId: item.technical_career.program_type?.id,
            programTypeName: item.technical_career.program_type?.name || '',
            requirements: item.technical_career.requirements || [],
          }));

        const merged = [...gradeOptions, ...technicalOptions];

        const unique = merged.filter(
          (item, index, self) => index === self.findIndex((el) => el.key === item.key)
        );

        setProgramTypes(unique);
      } catch (error) {
        console.error(error);
      }
    };

    if (id_est) {
      getStudentProgramsForDuplicate();
    }
  }, [id_est]);

  React.useEffect(() => {
    if (!targetProgramKey) {
      setTargetRequirements([]);
      setTargetRequirement('');
      setTargetRequirementsMany([]);
      return;
    }

    const selected = programTypes.find((el) => el.key === targetProgramKey);

    if (selected) {
      setTargetRequirements(
        (selected.requirements || []).map((req) => ({
          ...req,
          label: req.name,
          value: req.id,
        }))
      );
      setTargetRequirement('');
      setTargetRequirementsMany([]);
    }
  }, [targetProgramKey, programTypes]);

  const resetCreateModal = () => {
    setMethod('');
    setRequirementSelect('');
    setImageUrl('');
    setApprovedPreviousYear(false);
    if (inputEl.current) inputEl.current.value = '';
  };

  const resetDuplicateModal = () => {
    setRequirementToDuplicate(null);
    setTargetProgramKey('');
    setTargetRequirements([]);
    setTargetRequirement('');
    setTargetRequirementsMany([]);
    setDuplicateMode('single');
  };

  const openDuplicateModal = (requeriment) => {
    setRequirementToDuplicate(requeriment);
    setTargetProgramKey('');
    setTargetRequirements([]);
    setTargetRequirement('');
    setTargetRequirementsMany([]);

    const hasMany = Array.isArray(requeriment.requirements_many) && requeriment.requirements_many.length > 0;
    setDuplicateMode(hasMany ? 'many' : 'single');

    setDuplicateOpen(true);
  };

  const handleRequeriment = async () => {
    if (!requirementSelect || (!approvedPreviousYear && !method)) {
      alert('No deje ningún campo vacío');
      return;
    }

    try {
      setLoading(true);

      const response = await xhr.post('/requirement-sittings/', {
        data: {
          method: approvedPreviousYear ? null : method,
          requirement: requirementSelect,
          student: id_est,
          status: approvedPreviousYear ? 'approved' : 'pending',
          program_type: searchParams.get('programType'),
          school: user?.school?.id,
          approved_previous_year: approvedPreviousYear,
        },
      });

      const newId = response?.data?.data?.id;
      const responsePopulate = await xhr(`/requirement-sittings/${newId}?populate=*`);

      if (imageUrl) {
        const formData = new FormData();
        formData.append('files', imageUrl);
        formData.append('ref', 'api::requirement-sitting.requirement-sitting');
        formData.append('refId', newId);
        formData.append('field', 'file');

        const uploadResponse = await xhr('/upload', {
          method: 'post',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setRequeriments((prev) => [
          { ...responsePopulate.data.data, file: uploadResponse.data[0] },
          ...prev,
        ]);
      } else {
        setRequeriments((prev) => [responsePopulate.data.data, ...prev]);
      }

      toast({
        status: 'success',
        title: 'El requisito se agregó correctamente',
        position: 'bottom',
      });

      resetCreateModal();
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: 'No se pudo agregar el requisito',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleRequirementMany = (id) => {
    setTargetRequirementsMany((prev) => {
      if (prev.includes(id)) {
        return prev.filter((el) => el !== id);
      }
      return [...prev, id];
    });
  };

  const handleDuplicate = async () => {
    if (!requirementToDuplicate || !targetProgramKey) {
      alert('No deje ningún campo vacío');
      return;
    }

    if (duplicateMode === 'single' && !targetRequirement) {
      alert('Debe seleccionar un requisito destino');
      return;
    }

    if (duplicateMode === 'many' && !targetRequirementsMany.length) {
      alert('Debe seleccionar al menos un requisito destino');
      return;
    }

    try {
      setDuplicateLoading(true);

      const selectedProgram = programTypes.find((el) => el.key === targetProgramKey);

      if (!selectedProgram?.programTypeId) {
        throw new Error('No se encontró el program_type del programa seleccionado');
      }

      const payload = {
        sourceRequirementSittingId: requirementToDuplicate.id,
        targetProgramTypeId: selectedProgram.programTypeId,
      };

      if (duplicateMode === 'single') {
        payload.targetRequirementId = targetRequirement;
      } else {
        payload.targetRequirementsManyIds = targetRequirementsMany;
      }

      const response = await xhr.post('/requirement-sittings/duplicate-with-file', payload);

      const newItem = response?.data;

      if (String(selectedProgram.programTypeId) === String(currentProgramType) && newItem) {
        await loadCurrentRequirements();
      }

      toast({
        status: 'success',
        title: 'El requisito se duplicó correctamente',
        position: 'bottom',
      });

      resetDuplicateModal();
      setDuplicateOpen(false);
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: 'No se pudo duplicar el requisito',
        position: 'bottom',
      });
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDelete = async (requeriment) => {
    try {
      setRequeriments((prev) => prev.filter((el) => el.id !== requeriment.id));

      await xhr.delete(`/requirement-sittings/${requeriment.id}`);

      if (requeriment.file?.id) {
        await xhr.delete(`/upload/files/${requeriment.file.id}`);
      }

      toast({
        status: 'success',
        title: 'El requisito se eliminó correctamente',
        position: 'bottom',
      });
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: 'No se pudo eliminar el requisito',
        position: 'bottom',
      });
      await loadCurrentRequirements();
    }
  };

  const availableProgramTypes = programTypes.filter(
    (el) => String(el.programTypeId) !== String(currentProgramType)
  );

  return (
    <Box>
      <Link to={`/plataforma/administrador/ver-estudiante/${id_est}`}>
        <a>Volver</a>
      </Link>

      <Box>
        <Flex alignItems="center" justifyContent="space-between">
          <Heading as="h1" margin="0">
            Requisitos
          </Heading>

          <BasicModal setIsOpen={setIsOpen} isOpen={isOpen}>
            <Box>
              <Box>Requisito</Box>
              <Select
                value={requirementSelect}
                onChange={(e) => setRequirementSelect(e.target.value)}
                name="requirement"
                className={`${globals.input} ${globals.nombres}`}
              >
                <option value="">Seleccione</option>
                {programs.map((el) => (
                  <option key={el.value} value={el.value}>
                    {stripHtml(el.label)}
                  </option>
                ))}
              </Select>

              <Box mt="10px">
                <Checkbox
                  isChecked={approvedPreviousYear}
                  onChange={(e) => setApprovedPreviousYear(e.target.checked)}
                  colorScheme="purple"
                >
                  Aprobado año anterior
                </Checkbox>
              </Box>

              {!approvedPreviousYear && (
                <>
                  <Box mt="10px">Método</Box>
                  <Select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    name="method"
                    className={`${globals.input} ${globals.nombres}`}
                    mb="20px"
                  >
                    <option value="">Seleccione</option>
                    <option value="fisical">Físico</option>
                    <option value="scanned">Escaneado</option>
                  </Select>

                  <label htmlFor="img">Imagen</label>
                  <input type="file" name="file" onChange={handleFileInput} ref={inputEl} />
                </>
              )}
              <br />

              <Button
                type="submit"
                colorScheme="messenger"
                backgroundColor="#574feb"
                width="140px"
                isLoading={loading}
                mt="20px"
                onClick={handleRequeriment}
              >
                Crear
              </Button>
            </Box>
          </BasicModal>

          <BasicModal setIsOpen={setDuplicateOpen} isOpen={duplicateOpen}>
            <Box>
              <Heading as="h3" size="md" mb="15px">
                Duplicar requisito
              </Heading>

              {requirementToDuplicate && (
                <Box mb="10px">
                  Origen:{' '}
                  <strong>
                    {stripHtml(
                      requirementToDuplicate.requirement?.name ||
                      requirementToDuplicate.requirements_many
                        ?.map((el) => stripHtml(el.name))
                        .join(', ') ||
                      'Requisito'
                    )}
                  </strong>
                </Box>
              )}

              <Box mb="10px">Tipo de duplicación</Box>
              <Box mb="15px">
                {duplicateMode === 'single'
                  ? 'Requisito único'
                  : 'Múltiples requisitos'}
              </Box>

              <Box mb="10px">Programa destino</Box>
              <Select
                value={targetProgramKey}
                onChange={(e) => setTargetProgramKey(e.target.value)}
                className={`${globals.input} ${globals.nombres}`}
                mb="15px"
              >
                <option value="">Seleccione</option>
                {availableProgramTypes.map((el) => (
                  <option key={el.key} value={el.key}>
                    {stripHtml(el.programName)} - {stripHtml(el.programTypeName)}
                  </option>
                ))}
              </Select>

              {duplicateMode === 'single' ? (
                <>
                  <Box mb="10px">Requisito destino</Box>
                  <Select
                    value={targetRequirement}
                    onChange={(e) => setTargetRequirement(e.target.value)}
                    className={`${globals.input} ${globals.nombres}`}
                    mb="20px"
                    isDisabled={!targetProgramKey}
                  >
                    <option value="">Seleccione</option>
                    {targetRequirements.map((el) => (
                      <option key={el.value} value={el.value}>
                        {stripHtml(el.label)}
                      </option>
                    ))}
                  </Select>
                </>
              ) : (
                <>
                  <Box mb="10px">Requisitos destino</Box>
                  <Box
                    border="1px solid #E2E8F0"
                    borderRadius="6px"
                    p="12px"
                    mb="20px"
                    maxH="220px"
                    overflowY="auto"
                    opacity={!targetProgramKey ? 0.6 : 1}
                  >
                    <Stack spacing={2}>
                      {targetRequirements.map((el) => (
                        <Checkbox
                          key={el.value}
                          isChecked={targetRequirementsMany.includes(el.value)}
                          onChange={() => toggleRequirementMany(el.value)}
                          isDisabled={!targetProgramKey}
                        >
                          {stripHtml(el.label)}
                        </Checkbox>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}

              <Button
                backgroundColor="#574feb"
                color="white"
                width="160px"
                isLoading={duplicateLoading}
                onClick={handleDuplicate}
              >
                Duplicar
              </Button>
            </Box>
          </BasicModal>

          <Button colorScheme="messenger" variant="outline" onClick={() => setIsOpen(true)}>
            + Requisito
          </Button>
        </Flex>

        <TableContainer>
          <Table variant="striped" colorScheme="teal">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Detalle</Th>
                <Th>Método</Th>
                <Th>Estado</Th>
                <Th>Archivo</Th>
                <Th textAlign="right">Fecha</Th>
                <Th textAlign="right">Acciones</Th>
              </Tr>
            </Thead>

            <Tbody>
              {requeriments.map((requeriment) => (
                <Tr key={requeriment.id}>
                  <Td>{requeriment.id}</Td>

                  <Td>
                    {requeriment.requirement ? (
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: requeriment.requirement.name,
                        }}
                      />
                    ) : (
                      (requeriment.requirements_many || []).map((el) => (
                        <Box
                          key={el.id}
                          dangerouslySetInnerHTML={{
                            __html: el.name,
                          }}
                        />
                      ))
                    )}
                  </Td>

                  <Td>{methodMap[requeriment.method] || requeriment.method}</Td>
                  <Td>
                    {statusMap[requeriment.status] || requeriment.status}
                    {requeriment.approved_previous_year && (
                      <Box as="span" ml="6px" px="6px" py="1px" bg="#E9D8FD" color="#6B46C1" borderRadius="full" fontSize="xs">
                        Año anterior
                      </Box>
                    )}
                    {requeriment.status === 'denied' && requeriment.comment && (
                      <Box fontSize="xs" color="red.600" mt="4px" whiteSpace="normal" maxW="260px">
                        Motivo: {requeriment.comment}
                      </Box>
                    )}
                  </Td>

                  <Td>
                    {requeriment.file && (
                      <a href={requeriment.file.url} target="_blank" rel="noreferrer">
                        Ver
                      </a>
                    )}
                  </Td>

                  <Td textAlign="right">
                    {dayjs(requeriment.createdAt).format('DD-MM-YYYY')}
                  </Td>

                  <Td textAlign="right">
                    <Flex justifyContent="flex-end" gap="10px">
                      <Button onClick={() => openDuplicateModal(requeriment)}>
                        Duplicar
                      </Button>

                      <Button
                        onClick={() => {
                          const response = window.confirm(
                            '¿Estás seguro(a) que deseas eliminar el Requisito?'
                          );

                          if (response) handleDelete(requeriment);
                        }}
                      >
                        X
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {!requeriments[0] && (
          <Box padding="20px">Este estudiante aún no ha enviado requisitos</Box>
        )}
      </Box>
    </Box>
  );
}